import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Check, HandCoins, Undo2, CalendarDays, Bell, Search } from 'lucide-react';
import { bcPaymentsService, specialEventsService } from '../../services';
import { LoadingPage } from '../../components/ui/Loading';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { useConfirmation } from '../../hooks/useModals';
import { formatGold } from '../../utils/goldNotation';

const ROLE_LABELS = { ATAQUE: 'Ataque', BUFF: 'Buff', SUPORTE: 'Suporte' };
const PROOF_LABELS = {
  APROVADO: { text: 'Prova aprovada', className: 'bg-green-500/20 text-green-400 border-green-500/30' },
  AGUARDANDO_APROVACAO: { text: 'Aguardando aprovação', className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
  SEM_PROVA: { text: 'Sem prova', className: 'bg-red-500/20 text-red-400 border-red-500/30' },
  NAO_APLICAVEL: { text: 'Não aplicável', className: 'bg-gray-500/20 text-gray-400 border-gray-500/30' },
};
const PROOF_BLOCKS_PAYMENT = ['SEM_PROVA', 'AGUARDANDO_APROVACAO'];
const RESOLVED_BADGES = {
  PAID: { text: 'Pago', className: 'bg-green-500/20 text-green-400 border-green-500/30' },
  DONATED: { text: 'Doado', className: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
  COMPULSORY: { text: 'Doação compulsória', className: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
};
const resolvedBadge = (b) => RESOLVED_BADGES[b.compulsoryDonation ? 'COMPULSORY' : b.status] || RESOLVED_BADGES.DONATED;
const STATUS_FILTERS = [
  { value: '', label: 'Todos os status' },
  { value: 'PENDING', label: 'Pendente' },
  { value: 'PAID', label: 'Pago' },
  { value: 'DONATED', label: 'Doado' },
  { value: 'COMPULSORY', label: 'Doação compulsória' },
  { value: 'EXCLUDED', label: 'Excluído' },
];
const EMPTY_FILTERS = { search: '', role: '', status: '', proof: '' };

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const formatIsoDate = (iso) => (iso ? iso.slice(0, 10).split('-').reverse().join('/') : '');

const matchesStatus = (b, status) => {
  switch (status) {
    case 'PENDING': return b.status === 'PENDING' && !b.excluded;
    case 'PAID': return b.status === 'PAID';
    case 'DONATED': return b.status === 'DONATED' && !b.compulsoryDonation;
    case 'COMPULSORY': return Boolean(b.compulsoryDonation);
    case 'EXCLUDED': return Boolean(b.excluded);
    default: return true;
  }
};

const BcPaymentPage = () => {
  const { modalState: confirmState, confirm, close: closeConfirm } = useConfirmation();
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [specialEvent, setSpecialEvent] = useState(null);
  const [payment, setPayment] = useState(null);

  const [prizeInput, setPrizeInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [paymentDateInput, setPaymentDateInput] = useState('');
  const [filters, setFilters] = useState(EMPTY_FILTERS);

  const [manualForm, setManualForm] = useState({ characterName: '', recipientName: '', role: 'ATAQUE' });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const eventData = await specialEventsService.getById(id);
      setSpecialEvent(eventData.event || eventData);

      try {
        const existingPayment = await bcPaymentsService.getBySpecialEvent(id);
        setPayment(existingPayment);
        setPrizeInput(String(existingPayment.prizeAmount));
        setPaymentDateInput(existingPayment.paymentDate ? existingPayment.paymentDate.slice(0, 10) : '');
      } catch (err) {
        if (err.response?.status !== 404) {
          throw err;
        }
        setPayment(null);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao carregar dados do pagamento BC.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleGenerate = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');
      const created = await bcPaymentsService.generate(id, { prizeAmount: prizeInput });
      setPayment(created);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao gerar pagamento BC.');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePrize = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');
      const updated = await bcPaymentsService.update(payment.id, { prizeAmount: prizeInput });
      setPayment(updated);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao atualizar pagamento BC.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddManual = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');
      const updated = await bcPaymentsService.addBeneficiary(payment.id, manualForm);
      setPayment(updated);
      setManualForm({ characterName: '', recipientName: '', role: 'ATAQUE' });
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao incluir beneficiário.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleExcluded = async (beneficiary) => {
    try {
      setError('');
      const updated = await bcPaymentsService.toggleExcluded(payment.id, beneficiary.id, !beneficiary.excluded);
      setPayment(updated);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao atualizar beneficiário.');
    }
  };

  const handleRemoveManual = async (beneficiary) => {
    try {
      setError('');
      const updated = await bcPaymentsService.removeBeneficiary(payment.id, beneficiary.id);
      setPayment(updated);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao remover beneficiário.');
    }
  };

  const handlePay = async (beneficiary) => {
    try {
      setError('');
      const updated = await bcPaymentsService.payBeneficiary(payment.id, beneficiary.id);
      setPayment(updated);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao marcar como pago.');
    }
  };

  const handleDonate = async (beneficiary) => {
    try {
      setError('');
      const updated = await bcPaymentsService.donateBeneficiary(payment.id, beneficiary.id);
      setPayment(updated);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao registrar doação.');
    }
  };

  const handleSetPaymentDate = async (e) => {
    e.preventDefault();
    const confirmed = await confirm({
      title: 'Definir data do pagamento',
      message: `Definir o pagamento para ${formatIsoDate(paymentDateInput)} e notificar os beneficiários pendentes?`,
      confirmText: 'Definir e notificar',
      type: 'info',
      icon: '📅'
    });
    if (!confirmed) return;
    try {
      setSaving(true);
      setError('');
      const { notification, ...updated } = await bcPaymentsService.setPaymentDate(payment.id, paymentDateInput);
      setPayment(updated);
      setSuccess(`Data definida. Notificação enviada para ${notification?.users ?? 0} beneficiário(s).`);
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao definir a data de pagamento.');
    } finally {
      setSaving(false);
    }
  };

  const handleCompulsoryDonation = async () => {
    const pendingCount = (payment.beneficiaries || []).filter((b) => b.status === 'PENDING' && !b.excluded).length;
    const confirmed = await confirm({
      title: 'Doação compulsória',
      message: `Doar ao caixa a cota dos ${pendingCount} beneficiário(s) que não receberam? Essa ação marca todos como doação compulsória.`,
      confirmText: 'Doar ao caixa',
      type: 'danger',
      icon: '💰'
    });
    if (!confirmed) return;
    try {
      setSaving(true);
      setError('');
      const updated = await bcPaymentsService.compulsoryDonation(payment.id);
      setPayment(updated);
      setSuccess('Doação compulsória registrada.');
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao registrar doação compulsória.');
    } finally {
      setSaving(false);
    }
  };

  const filteredBeneficiaries = useMemo(() => {
    const term = filters.search.trim().toLowerCase();
    return (payment?.beneficiaries || []).filter((b) =>
      (!term || b.characterName?.toLowerCase().includes(term) || b.recipientName?.toLowerCase().includes(term))
      && (!filters.role || b.role === filters.role)
      && matchesStatus(b, filters.status)
      && (!filters.proof || b.proofStatus === filters.proof)
    );
  }, [payment, filters]);

  const handleUndo = async (beneficiary) => {
    try {
      setError('');
      const updated = await bcPaymentsService.undoBeneficiary(payment.id, beneficiary.id);
      setPayment(updated);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao desfazer.');
    }
  };

  if (loading) return <LoadingPage />;

  const isLocked = payment?.beneficiaries?.some((b) => b.status !== 'PENDING');
  const pendingCount = (payment?.beneficiaries || []).filter((b) => b.status === 'PENDING' && !b.excluded).length;
  const canCompulsoryDonate = Boolean(payment?.paymentDate) && todayIso() >= payment.paymentDate.slice(0, 10) && pendingCount > 0;
  const hasActiveFilters = Object.values(filters).some(Boolean);
  const linkedEvent = specialEvent?.linkedEvent;

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(`/app/admin/special-events/${id}`)}
            className="p-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 transition-all"
          >
            <ArrowLeft className="w-5 h-5 text-orange-400" />
          </button>
          <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent">
            💰 Pagamento BC — {specialEvent?.name}
          </h1>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/50 rounded-xl p-4">
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {success && (
        <div className="bg-green-500/10 border border-green-500/50 rounded-xl p-4">
          <p className="text-green-400">{success}</p>
        </div>
      )}

      {!payment ? (
        <form
          onSubmit={handleGenerate}
          className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl space-y-4"
        >
          <h2 className="text-xl font-bold text-white">Gerar relatório de pagamento</h2>
          <Input
            label="Valor da premiação (gold)"
            placeholder="ex: 1.5kk"
            value={prizeInput}
            onChange={(e) => setPrizeInput(e.target.value)}
            variant="dark"
            required
          />
          <Button type="submit" loading={saving} disabled={!linkedEvent}>Gerar relatório</Button>
        </form>
      ) : (
        <>
          <form
            onSubmit={handleUpdatePrize}
            className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl space-y-4"
          >
            <h2 className="text-xl font-bold text-white">Premiação</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Input
                label="Valor total"
                value={prizeInput}
                onChange={(e) => setPrizeInput(e.target.value)}
                disabled={isLocked}
                variant="dark"
              />
              <div className="flex items-end">
                {!isLocked && <Button type="submit" loading={saving}>Salvar</Button>}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4 text-center pt-4 border-t border-white/10">
              <div>
                <p className="text-gray-400 text-xs">Custo administrativo (30%)</p>
                <p className="text-white font-bold" title={Number(payment.adminCostAmount).toLocaleString('pt-BR')}>{formatGold(payment.adminCostAmount)}</p>
              </div>
              <div>
                <p className="text-gray-400 text-xs">Caixa do clube (20%)</p>
                <p className="text-white font-bold" title={Number(payment.treasuryAmount).toLocaleString('pt-BR')}>{formatGold(payment.treasuryAmount)}</p>
              </div>
              <div>
                <p className="text-gray-400 text-xs">Pilotos (50%)</p>
                <p className="text-white font-bold" title={Number(payment.pilotsShareAmount).toLocaleString('pt-BR')}>{formatGold(payment.pilotsShareAmount)}</p>
              </div>
            </div>
          </form>

          <form
            onSubmit={handleSetPaymentDate}
            className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl space-y-4"
          >
            <h2 className="text-xl font-bold text-white flex items-center gap-2"><CalendarDays className="w-5 h-5" /> Data do pagamento</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
              <Input
                label="Data"
                type="date"
                value={paymentDateInput}
                min={todayIso()}
                onChange={(e) => setPaymentDateInput(e.target.value)}
                variant="dark"
                required
              />
              <div>
                <Button type="submit" loading={saving} disabled={!paymentDateInput}>
                  <Bell className="w-4 h-4" /> {payment.paymentDate ? 'Alterar e notificar' : 'Definir e notificar'}
                </Button>
              </div>
              <div>
                <Button
                  type="button"
                  variant="danger"
                  loading={saving}
                  disabled={!canCompulsoryDonate}
                  onClick={handleCompulsoryDonation}
                  title={canCompulsoryDonate ? '' : 'Disponível a partir da data de pagamento, se houver beneficiários pendentes'}
                >
                  <HandCoins className="w-4 h-4" /> Doação compulsória ({pendingCount})
                </Button>
              </div>
            </div>
            <p className="text-xs text-gray-400">
              {payment.paymentDate
                ? `Pagamento em ${formatIsoDate(payment.paymentDate)}. `
                : 'Nenhuma data definida. '}
              Ao definir a data, só os beneficiários pendentes recebem a notificação. Quem não receber até a data pode ter a cota doada ao caixa pela doação compulsória.
            </p>
          </form>

          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Input
                placeholder="Buscar personagem ou beneficiário..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                leftIcon={<Search className="w-4 h-4" />}
                variant="dark"
              />
              <Select value={filters.role} onChange={(e) => setFilters({ ...filters, role: e.target.value })}>
                <option value="">Todas as funções</option>
                {Object.entries(ROLE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </Select>
              <Select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
                {STATUS_FILTERS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </Select>
              <Select value={filters.proof} onChange={(e) => setFilters({ ...filters, proof: e.target.value })}>
                <option value="">Todas as provas</option>
                {Object.entries(PROOF_LABELS).map(([value, { text }]) => (
                  <option key={value} value={value}>{text}</option>
                ))}
              </Select>
            </div>
            <div className="flex items-center justify-between text-sm text-gray-400">
              <span>{filteredBeneficiaries.length} de {(payment.beneficiaries || []).length} beneficiário(s)</span>
              {hasActiveFilters && (
                <button type="button" onClick={() => setFilters(EMPTY_FILTERS)} className="text-orange-400 hover:text-orange-300">
                  Limpar filtros
                </button>
              )}
            </div>
          </div>

          {hasActiveFilters && filteredBeneficiaries.length === 0 && (
            <p className="text-center text-gray-400">Nenhum beneficiário encontrado com esses filtros.</p>
          )}

          {['ATAQUE', 'BUFF', 'SUPORTE'].map((role) => {
            const rows = filteredBeneficiaries.filter((b) => b.role === role);
            if (rows.length === 0) return null;

            return (
              <div key={role} className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl">
                <h2 className="text-xl font-bold text-white mb-4">{ROLE_LABELS[role]} ({rows.length})</h2>
                <div className="space-y-2">
                  {rows.map((beneficiary) => {
                    const proof = PROOF_LABELS[beneficiary.proofStatus] || PROOF_LABELS.NAO_APLICAVEL;
                    return (
                      <div
                        key={beneficiary.id}
                        className={`flex flex-wrap items-center justify-between gap-3 p-3 rounded-xl border ${
                          beneficiary.excluded ? 'border-white/5 bg-white/5 opacity-50' : 'border-white/10 bg-white/5'
                        }`}
                      >
                        <div className="min-w-[180px]">
                          <p className="text-white font-medium">{beneficiary.characterName}</p>
                          <p className="text-gray-400 text-sm">Beneficiário: {beneficiary.recipientName}</p>
                        </div>
                        <span className={`px-2 py-1 rounded-lg text-xs border ${proof.className}`}>{proof.text}</span>
                        <p className="text-white font-bold" title={Number(beneficiary.shareAmount).toLocaleString('pt-BR')}>{formatGold(beneficiary.shareAmount)}</p>
                        <div className="flex gap-2 flex-wrap">
                          {beneficiary.status === 'PENDING' && !beneficiary.excluded && (
                            <>
                              <Button
                                size="sm"
                                variant="success"
                                disabled={PROOF_BLOCKS_PAYMENT.includes(beneficiary.proofStatus)}
                                onClick={() => handlePay(beneficiary)}
                              >
                                <Check className="w-4 h-4" /> Pagar
                              </Button>
                              <Button size="sm" variant="secondary" onClick={() => handleDonate(beneficiary)}>
                                <HandCoins className="w-4 h-4" /> Doar
                              </Button>
                            </>
                          )}
                          {beneficiary.status !== 'PENDING' && (
                            <>
                              <span className={`px-2 py-1 rounded-lg text-xs border ${resolvedBadge(beneficiary).className}`}>
                                {resolvedBadge(beneficiary).text}
                              </span>
                              <Button size="sm" variant="ghost" onClick={() => handleUndo(beneficiary)}>
                                <Undo2 className="w-4 h-4" /> Desfazer
                              </Button>
                            </>
                          )}
                          {beneficiary.status === 'PENDING' && beneficiary.source === 'AUTO' && (
                            <Button size="sm" variant="outline" onClick={() => handleToggleExcluded(beneficiary)}>
                              {beneficiary.excluded ? 'Reincluir' : 'Excluir'}
                            </Button>
                          )}
                          {beneficiary.status === 'PENDING' && beneficiary.source === 'MANUAL' && (
                            <Button size="sm" variant="danger" onClick={() => handleRemoveManual(beneficiary)}>
                              Remover
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          <form
            onSubmit={handleAddManual}
            className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl space-y-4" hidden={true}
          >
            <h2 className="text-xl font-bold text-white flex items-center gap-2"><Plus className="w-5 h-5" /> Incluir beneficiário manualmente</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Input
                label="Nome do personagem"
                value={manualForm.characterName}
                onChange={(e) => setManualForm({ ...manualForm, characterName: e.target.value })}
                variant="dark"
                required
              />
              <Input
                label="Nome do beneficiário (piloto/dono)"
                value={manualForm.recipientName}
                onChange={(e) => setManualForm({ ...manualForm, recipientName: e.target.value })}
                variant="dark"
                required
              />
              <div>
                <label className="block text-sm font-semibold mb-2 text-white">Papel</label>
                <Select value={manualForm.role} onChange={(e) => setManualForm({ ...manualForm, role: e.target.value })}>
                  <option value="ATAQUE">Ataque</option>
                  <option value="BUFF">Buff</option>
                  <option value="SUPORTE">Suporte</option>
                </Select>
              </div>
            </div>
            <Button type="submit" loading={saving}>Incluir</Button>
          </form>
        </>
      )}

      <ConfirmationModal
        isOpen={confirmState.isOpen}
        onClose={closeConfirm}
        onConfirm={confirmState.onConfirm}
        title={confirmState.title}
        message={confirmState.message}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        type={confirmState.type}
        icon={confirmState.icon}
      />
    </div>
  );
};

export default BcPaymentPage;
